import { useQuery } from '@apollo/client';
import { isEmpty, isNil } from 'lodash';
import { useCallback, useMemo } from 'react';
import { Queries } from '@shared/api';
import { API } from '@shared/types/api';
import { notNil } from '@shared/util/notNil';

const CHANNEL_MEMBERS_PAGE_SIZE = 50;

interface UseChannelMembersListProps {
  channelId?: number;
  name?: string;
  isActive?: boolean;
  notMember?: boolean;
  /**
   * Keeps the last results while a new search loads, instead of dropping to an empty list. For
   * search-as-you-type lists, where each search has its own cache entry and starts with no data.
   */
  keepPreviousResults?: boolean;
}

interface UseChannelMembersListResult {
  users: API.ShortUserFragment[];
  total: number;
  hasMore: boolean;
  loading: boolean;
  loadMore: () => void;
}

export function useChannelMembersList({
  channelId,
  name = '',
  isActive,
  notMember,
  keepPreviousResults = false,
}: UseChannelMembersListProps): UseChannelMembersListResult {
  const trimmedName = name.trim();
  const customCacheKey = getChannelMembersListCacheKey({
    channelId,
    isActive,
    name: trimmedName,
    notMember,
  });

  const input = useMemo(() => {
    if (isNil(channelId)) {
      return {};
    }
    const memberOfChannelIds = !notMember ? [channelId] : undefined;
    const notMemberOfChannelIds = notMember ? [channelId] : undefined;
    return {
      memberOfChannelIds,
      notMemberOfChannelIds,
      limit: CHANNEL_MEMBERS_PAGE_SIZE,
      offset: 0,
      excludeSystemUsers: true,
      orderBys: [{ orderBy: 'firstName' as const, direction: 'ASC' as const }],
      ...(isNil(isActive) ? {} : { isActive }),
      ...(isEmpty(trimmedName) ? {} : { name: trimmedName }),
    };
  }, [channelId, isActive, notMember, trimmedName]);

  const { data, previousData, loading, fetchMore } = useQuery<
    API.ListUsersQuery,
    API.ListUsersQueryVariables
  >(Queries.user.listUsers, {
    variables: { input, customCacheKey },
    fetchPolicy: 'cache-and-network',
    skip: isNil(channelId),
  });

  const loadMore = useCallback(async () => {
    const offset = data?.listUsers?.users?.length ?? 0;
    if (data?.listUsers?.hasMore) {
      await fetchMore({
        variables: {
          input: { ...input, offset },
          customCacheKey,
        },
      });
    }
  }, [customCacheKey, data?.listUsers, fetchMore, input]);

  // `previousData` belongs to this hook instance, not the cache, so it survives a change of search
  const listUsers = data?.listUsers ?? (keepPreviousResults ? previousData?.listUsers : undefined);

  return useMemo(() => {
    return {
      users: listUsers?.users ?? [],
      hasMore: listUsers?.hasMore ?? false,
      total: listUsers?.total ?? 0,
      loading,
      loadMore,
    };
  }, [listUsers, loading, loadMore]);
}

export function getChannelMembersListCacheKeyPrefix(channelId: number): string {
  return ['channel-members', channelId].join('-');
}

function getChannelMembersListCacheKey({
  channelId,
  isActive,
  name,
  notMember,
}: Pick<UseChannelMembersListProps, 'channelId' | 'isActive' | 'name' | 'notMember'>): string {
  if (isNil(channelId)) {
    return '';
  }

  const prefix = getChannelMembersListCacheKeyPrefix(channelId);
  return [prefix, notMember, isActive, name].filter(notNil).join('-');
}
